<template>
  <div class="page-container">
    <h1 class="page-title">{{ t('pages.company.partners.title') }}</h1>
    <!-- <p class="page-subtitle">{{ t('pages.company.partners.subtitle') }}</p> -->

    <!-- 布局控制选项 -->
    <!-- <div class="layout-controls">
      <el-switch
        v-model="enableCarousel"
        :active-text="'轮播模式'"
        :inactive-text="'网格模式'"
        class="switch-control"
      />
      <el-slider
        v-if="enableCarousel" 
        v-model="scrollSpeed" 
        :min="10" 
        :max="50" 
        :step="5"
        :format-tooltip="value => `${value}s`"
        style="width: 200px; margin-left: 20px;"
      />
    </div> -->

    <!-- 合作伙伴展示区 -->
    <div class="partner-grid-container">
      <!-- 合作伙伴网格 - 静态展示 -->
      <div v-if="!enableCarousel" class="partner-grid">
        <div class="partner-card" v-for="(partner, index) in partnerList" :key="index" @dblclick="showBigImage(partner.url)">
          <div class="image-container">
            <img :src="partner.url" :alt="partner.alt">
          </div>
          <div class="partner-info">
            <div class="partner-name">{{ partner.altdetail }}</div>
            <div class="partner-desc" v-if="partner.desc">{{ partner.desc }}</div>
          </div>
        </div>
      </div>
      
      <!-- 合作伙伴网格 - 带轮播效果 -->
      <div v-else class="carousel-container" ref="carouselContainer">
        <div 
          :class="['partner-grid', { 'carousel-auto': autoScroll }]" 
          ref="partnerGrid"
          :style="{ 'animation-duration': `${scrollSpeed}s` }"
        >

          <template v-for="(partner, index) in duplicatedPartners" :key="index">
            <div class="partner-card" @dblclick="showBigImage(partner.url)">
              <div class="image-container">
                <img :src="partner.url" :alt="partner.alt">
              </div>
              <div class="partner-info">
                <div class="partner-name">{{ partner.altdetail }}</div>
                <div class="partner-desc" v-if="partner.desc">{{ partner.desc }}</div>
              </div>
            </div>
          </template>
        </div>
        <div class="carousel-mask" v-if="enableCarousel"></div>
      </div>
      
      <!-- 轮播控制区 -->
      <!-- <div class="carousel-controls" v-if="enableCarousel">
        <button class="control-btn" @click="toggleAutoScroll">
         
          <svg v-if="autoScroll" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
          </svg>
          <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </button>
      </div> -->
    </div>

    <!-- 大图显示层 -->
    <div v-if="bigImageVisible" class="big-image-overlay" @click="hideBigImage">
      <img :src="currentBigImageUrl" alt="Enlarged image" class="enlarged-image" @click.stop />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
// 导入模块
import zhData from '../../../i18n/zh';
import enData from '../../../i18n/en';
import { useLangStore } from '../../../pinia/stores/lang';

const { t } = useI18n();
const langStore = useLangStore();

// 大图显示相关
const bigImageVisible = ref(false);
const currentBigImageUrl = ref('');

const showBigImage = (imageUrl: string) => {
  currentBigImageUrl.value = imageUrl;
  bigImageVisible.value = true;
};

const hideBigImage = () => {
  bigImageVisible.value = false;
  currentBigImageUrl.value = '';
};

// 根据当前语言选择对应的翻译数据
const translationData = computed(() => {
  // 根据langStore中的语言设置选择对应的翻译数据
  console.log('当前语言:', langStore.currentLang);
  return langStore.currentLang.startsWith('zh') ? zhData : enData;
});

// 获取合作伙伴数据
const partnerList = computed(() => {
  // 从选择的翻译数据中获取合作伙伴数据
  const data = translationData.value.pages.company.partners.content;
  console.log('合作伙伴数据:', data);
  return data as {
    url: string;
    alt: string;
    altdetail: string;
    desc?: string;
  }[];
});

// 复制数据用于无缝轮播
const duplicatedPartners = computed(() => {
  // 创建足够多的副本以填充滚动区域
  const partners = partnerList.value || [];
  return [...partners, ...partners, ...partners];
});

// 轮播功能配置
const enableCarousel = ref(false); // 是否启用轮播效果
const autoScroll = ref(true); // 是否自动滚动
const scrollSpeed = ref(90); // 滚动速度，单位秒
const carouselContainer = ref<HTMLElement | null>(null);
const partnerGrid = ref<HTMLElement | null>(null);

// 检查是否需要启用轮播
function checkCarouselNeeded() {
  if (!carouselContainer.value || !partnerGrid.value) return;
  
  const containerHeight = carouselContainer.value.clientHeight;
  const gridHeight = partnerGrid.value.scrollHeight;
  
  // 如果网格高度超过容器，则启用轮播
  enableCarousel.value = gridHeight > containerHeight;
}

// 切换自动滚动
function toggleAutoScroll() {
  autoScroll.value = !autoScroll.value;
}

// 组件挂载后进行初始化
onMounted(() => {
  // 设置初始轮播状态
  checkCarouselNeeded();
  
  // 监听窗口大小变化以重新计算轮播需求
  window.addEventListener('resize', checkCarouselNeeded);
});

// 监听合作伙伴数据变化
watch(partnerList, () => {
  checkCarouselNeeded();
}, { deep: true });

// 监听语言变化
watch(() => langStore.currentLang, (newLang: string) => {
  console.log('语言变化:', newLang);
  // 语言变化时需要重新计算
  setTimeout(checkCarouselNeeded, 100);
});
</script>

<style scoped lang="scss">
// 页面特定样式
.page-container {
  padding: 0px 30px 40px 80px;
  
  @media screen and (max-width: 768px) {
    padding: 0px 15px 20px 15px;
  }
}
.page-subtitle {
  text-align: center;
  margin: -20px auto 30px;
  color: #666;
  font-size: 16px;
  max-width: 800px;
}

.carousel-container {
  height: 70vh; // 设置轮播容器高度
  margin-top: 30px;
}

.layout-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 30px;

  .switch-control {
    :deep(.el-switch__label) {
      color: #666;
    }
  }
}

// 自定义轮播样式
:deep(.carousel-auto) {
  animation-duration: v-bind('`${scrollSpeed}s`');
}

// 大图显示样式
.big-image-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  cursor: pointer;
}

.enlarged-image {
  padding: 50px;
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  cursor: default;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.3);
}

// 响应式优化
@media screen and (max-width: 768px) {
  .page-subtitle {
    font-size: 14px;
    margin: -15px auto 25px;
  }
  
  .carousel-container {
    height: 60vh;
  }
}
</style> 